<template>
  <div>
    <div class="mb-4 flex w-full flex-wrap items-center justify-between gap-2">
      <BaseSegmentedControl
        v-model="localFilters.status"
        :options="statusFilterOptions"
      />
      <BaseDateIntervalSelect
        :model-value="dateRange"
        @update:model-value="emit('update:dateRange', $event)"
      />
    </div>
    <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
      <BaseInputSearch v-model="localFilters.search" />
      <BasePagination v-model="page" :page-count="pageCount" class="ml-auto" />
    </div>
    <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
      <BaseCountSelect
        v-model="paginationCount"
        :total-count="data?.count || 0"
      />
    </div>
    <BaseTable
      key-field="id"
      :rows="rows"
      :columns="columns"
      :is-loading="isFetching"
    >
      <template #body-cell:at="{ value }">
        <BaseTableDatetimeCell :value="value" />
      </template>

      <template #body-cell:registration="{ value }">
        <p class="text-md font-bold uppercase">
          {{ value }}
        </p>
      </template>

      <template #body-cell:gate="{ value }">
        <p class="font-semibold text-black">{{ value.name }}</p>
        <p>{{ value.detail }}</p>
      </template>

      <template #body-cell:status="{ value }">
        <span
          class="whitespace-nowrap rounded-full bg-opacity-20 px-3 py-1 text-xs font-medium"
          :class="[`bg-${value.color}`, `text-${value.color}`]"
        >
          {{ value.label }}
        </span>
      </template>

      <template #body-cell:image="{ value }">
        <a
          v-if="value"
          :href="value"
          target="_blank"
          rel="noopener"
          title="Gate camera image"
        >
          <img
            :src="value"
            alt="Gate camera"
            class="h-12 w-auto rounded border border-gray-200 object-cover"
            loading="lazy"
          />
        </a>
        <span v-else class="text-gray-400">—</span>
      </template>
    </BaseTable>
  </div>
</template>

<script setup lang="ts">
import { useSiteColumn } from "@/services/locations/useSiteColumn";
import { useStorageCrossings } from "@/services/storage/listStorageCrossings";
import { storageVia, type StorageCrossing } from "@/services/storage/storage";
import { type ColumnDefinition } from "@/types/table";

type DateInterval = {
  start?: Date;
  end?: Date;
};
type Status = StorageCrossing["status"];

// Compound gate crossings, as the Entry Log's ANPR and Keypad tables show
// theirs. The caller owns the date range (see CameraEventsTable); `filters`
// scopes the table to one booking.
const props = withDefaults(
  defineProps<{
    dateRange: DateInterval;
    filters?: { bookingId?: string };
  }>(),
  { filters: () => ({}) },
);

const emit = defineEmits<{
  (e: "update:dateRange", value: DateInterval): void;
}>();

const CROSSING_STATUS_MAP: Record<Status, { label: string; color: string }> = {
  opened: { label: "Opened", color: "green" },
  pending: { label: "Waiting", color: "amber" },
  denied: { label: "Refused", color: "red" },
  expired: { label: "Timed out", color: "gray2" },
  superseded: { label: "Replaced", color: "gray2" },
};
const DENY_TEXT: Record<string, string> = {
  wrong_pin: "Wrong PIN",
  unknown: "Registration not recognised",
};

const reason = (crossing: StorageCrossing): string => {
  if (crossing.status === "denied") {
    return DENY_TEXT[crossing.denyReason] ?? crossing.denyReason;
  }
  const via = storageVia(crossing.via);
  return via ? `By ${via}` : "-";
};

const { siteColumns } = useSiteColumn<StorageCrossing>((row) => row.siteId);

const columns = computed<ColumnDefinition<StorageCrossing>[]>(() => [
  {
    label: "Timestamp",
    name: "at",
    getValue: (row) => row.at,
    autoWidth: true,
  },
  {
    label: "Vehicle Registration",
    name: "registration",
    getValue: (row) => row.registration,
    autoWidth: true,
  },
  ...siteColumns.value,
  ...(props.filters.bookingId
    ? []
    : [
        {
          label: "Customer",
          name: "customerName",
          getValue: (row: StorageCrossing) => row.customerName || "-",
          autoWidth: true,
        },
        {
          label: "Pitch",
          name: "pitchName",
          getValue: (row: StorageCrossing) => row.pitchName || "-",
          autoWidth: true,
        },
      ]),
  {
    label: "Status",
    name: "status",
    getValue: (row) => CROSSING_STATUS_MAP[row.status],
    autoWidth: true,
  },
  {
    label: "Reason",
    name: "reason",
    getValue: (row) => reason(row),
    autoWidth: true,
  },
  {
    label: "Gate",
    name: "gate",
    getValue: (row) => ({
      name: row.laneName,
      detail: `${row.laneKind === "vehicle" ? "Vehicle" : "On foot"}, ${
        row.direction === "in" ? "in" : "out"
      }`,
    }),
    autoWidth: true,
  },
  {
    label: "Image",
    name: "image",
    getValue: (row) => row.photoUrl ?? "",
    autoWidth: true,
  },
]);

const localFilters = reactive<{ status: Status | null; search: string }>({
  status: null,
  search: "",
});

const paginationCount = ref(100);

const { pagination, page, pageCount, resetPagination } = usePagination({
  limit: paginationCount,
  count: computed(() => data.value?.count || 0),
});

watch(
  () => [props.filters, props.dateRange, localFilters],
  () => resetPagination(),
  { deep: true, immediate: true },
);

const query = reactiveComputed(() => ({
  ...props.filters,
  ...localFilters,
  fromDt: props.dateRange.start,
  untilDt: props.dateRange.end,
  ...pagination.value,
}));

const { data, isFetching } = useStorageCrossings(query);

const rows = computed(() => data.value?.items ?? []);

const statusFilterOptions = [
  { label: "All", value: null },
  ...(Object.keys(CROSSING_STATUS_MAP) as Status[]).map((value) => ({
    label: CROSSING_STATUS_MAP[value].label,
    value,
  })),
];
</script>
